import { Link } from "react-router-dom"
import { format, isValid, parse } from "date-fns"
import { capitalize } from "lodash"
import htmlParse, { DOMNode, domToReact, Element } from "html-react-parser"
import { getRoutingMethod } from "@/utils/helpers/configs"
import { UseContentProps } from "@/utils/hooks/useContent"
import { ReactNode } from "react"

export function isLocal() {
  const host = window.location.hostname
  return /^(localhost|(?:[0-9]{1,3}\.){3}[0-9]{1,3}|local)/.test(host)
}

export function isUat() {
  return window.location.hostname === "mol-uat.gesb.wa.gov.au"
}

// UAT and live both webpack-build with NODE_ENV=production; hostname is the deploy signal for error reports
export function getDeployEnvironment(): "development" | "uat" | "production" {
  if (isLocal()) {
    return "development"
  }
  if (window.location.hostname.includes("mol-uat")) {
    return "uat"
  }
  return "production"
}

export function parseMatrixDate(str: string) {
  return parse(str, "yyyy-MM-dd HH:mm:ss", new Date())
}

export function parseLinkDate(str: string) {
  const dateFormats: string | readonly string[] = COMMON_DATE_FORMATS
  return parseDateWithFormats(str, dateFormats)
}

export function formatName(name: string) {
  const capitalized = name
    .trim()
    .split(" ")
    .map((s) => capitalize(s))

  // logic taken from statement summaries firstname hyphen handling
  capitalized[0] = capitalized[0]
    .split("-")
    .map((s) => capitalize(s))
    .join("-")

  return capitalized.join(" ")
}

const currency = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
})

// taken from statement summaries
export function formatMoney(value: number | bigint, unwrap: true): string
export function formatMoney(value: number | bigint, unwrap?: false): ReactNode
export function formatMoney(value: number | bigint, unwrap = false) {
  const formatted = currency.format(value)
  if (unwrap) {
    return formatted.replace("-", "- ")
  }
  return <span className="money-value">{formatted.replace("-", "- ")}</span>
}

// take in number value and $ format prepend with +/- depending
//  on either the value or the sign specified
export function formatMoneySigned(value: number | bigint, sign = null) {
  const str = currency.format(value).replace(/-/, "")
  return (
    <span className="money-value">
      {value === 0 ? str : `${sign ? sign : value > 0 ? "+" : "-"} ${str}`}
    </span>
  )
}

const currencyWhole = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
})

export function formatMoneyWhole(value: number | bigint, unwrap: true): string
export function formatMoneyWhole(
  value: number | bigint,
  unwrap?: false
): ReactNode
export function formatMoneyWhole(value: number | bigint, unwrap = false) {
  const formatted = currencyWhole.format(value).replace("-", "- ")
  if (unwrap) {
    return formatted
  }

  return <span className="money-value">{formatted}</span>
}

export function roundToNearest50K(value: number): number {
  return Math.round(value / 50000) * 50000
}

// Takes in a Date and formats to appear as
// Tuesday, 12 April 2022, 10:21am
export function formatHeaderDate(date: number | Date) {
  return format(date, "EEEE, d MMMM yyyy, h.mmaaa")
}

/** Formats to try when parsing date strings. Order matters for ambiguous inputs.
 * yyyy-MM-dd (ISO)
 * MM/dd/yyyy (US, e.g. 12/23/2025)
 * M/d/yyyy (US with single digits)
 * dd/MM/yyyy (AU/EU)
 * d/M/yyyy (AU/EU with single digits)
 *
 */
export const COMMON_DATE_FORMATS = [
  "yyyy-MM-dd",
  "MM/dd/yyyy",
  "M/d/yyyy",
  "dd/MM/yyyy",
  "d/M/yyyy",
] as const

/**
 * Normalizes loose date strings from APIs before parsing.
 * - Trims whitespace
 * - Adds a date-only candidate when extra tokens look like junk (e.g. `"4/2/2026 1"` → also tries `"4/2/2026"`)
 */
export function sanitizeDateStringForParsing(input: string): string[] {
  const trimmed = input.trim()
  if (!trimmed) {
    return []
  }
  const candidates: string[] = [trimmed]
  // Strip time from ISO 8601 datetime strings (e.g. "2026-07-10T09:13:46.777")
  const isoDatePart = trimmed.match(/^(\d{4}-\d{2}-\d{2})T/)?.[1]
  if (isoDatePart) {
    candidates.push(isoDatePart)
  }
  const tokens = trimmed.split(/\s+/)
  const first = tokens[0]
  if (tokens.length > 1 && first) {
    candidates.push(first)
  }
  return [...new Set(candidates)]
}

/**
 * Parse a date string using the first matching format.
 * @returns Parsed Date
 * @throws If no format successfully parses the string
 */
export function parseDateWithFormats(
  str: string,
  formats: readonly string[] | string = COMMON_DATE_FORMATS,
  referenceDate = new Date()
): Date {
  const formatList = typeof formats === "string" ? [formats] : [...formats]
  const candidates = sanitizeDateStringForParsing(str)

  if (candidates.length === 0) {
    throw new Error(
      `Could not parse date "${str}" with formats: ${formatList.join(", ")}`
    )
  }

  for (const candidate of candidates) {
    for (const fmt of formatList) {
      const result = parse(candidate, fmt, referenceDate)
      if (isValid(result)) {
        return result
      }
    }
  }

  throw new Error(
    `Could not parse date "${str}" with formats: ${formatList.join(", ")}`
  )
}

// Takes in a Date and formats to appear as
// 8 January 2020
export function formatSimpleDate(date: Date | number | string) {
  if ("string" === typeof date) {
    date = parseDateWithFormats(date)
  }
  return format(date, "d MMMM yyyy")
}

export function formatTableDate(date: Date | number | string) {
  if ("string" === typeof date) {
    date = parseDateWithFormats(date)
  }
  return format(date, "dd/MM/yyyy")
}

// abbreviated month per bank accounts widget wireframe (e.g. "12 Oct 2020"), distinct from formatSimpleDate's full month name
export function formatShortDate(date: Date | number | string) {
  if ("string" === typeof date) {
    date = parseDateWithFormats(date)
  }
  return format(date, "d MMM yyyy")
}

// format dates for Link api query
export function formatQueryDate(date: Date | number | string) {
  if ("string" === typeof date) {
    date = parseDateWithFormats(date)
  }
  return format(date, "yyyy-MM-dd")
}

// taken from statement summaries
export function formatPercent(n: number | bigint | string) {
  const s = (n + "").split(".")
  if (s.length > 1) {
    return `${s[0]}.${s[1].length === 1 ? s[1] + "0" : s[1]}%`
  }

  return s[0].length ? s[0] + "%" : "..."
}

// Takes a string and replaces curly brackets with object data
export function replacePropsInString(str: string, obj: any) {
  return (
    str
      // allow dynamic links in content using "mol_keyword_name" => "{keyword_name}"
      .replace(/href="mol_(.*?)"/g, 'href="{$1}"')
      // replace any values wrapped in {} with the matching prop key
      .replace(/\{(\w+)}/g, (_match, prop) => obj?.[prop] ?? `{${prop}}`)
  )
}

export function replaceFunction(node: any, options?: any): any {
  const { attribs, children, name } = node

  if (name === "div") {
    return <>{domToReact(children, options ?? { replace: replaceFunction })}</>
  }
  if (name === "a" && attribs.href?.includes(".pdf")) {
    const allClasses = attribs?.class?.split(" ") || []
    allClasses.push("pdf-icon")
    const otherAttrs = {
      ...attribs,
    }
    delete otherAttrs.class
    return (
      <a className={allClasses.join(" ")} {...otherAttrs}>
        {domToReact(children)}
        <span className="sr-only">Opens PDF in new window</span>
      </a>
    )
  }
  if (
    name === "a" &&
    (attribs.href?.includes("/app/") || attribs.href?.includes("#/"))
  ) {
    const allClasses = attribs?.class?.split(" ") || []
    const otherAttrs = {
      ...attribs,
    }

    const { host, basename } = window.GESB_MemberOnlineConfig

    delete otherAttrs.class

    // strips full urls down to react-router paths
    let appLink = attribs.href?.includes("/app/")
      ? attribs.href
          .replace(/(^\w+:|^)\/\//, "")
          .replace(host, "")
          .replace(basename, "")
      : attribs.href.replace("#", "")

    // allow Matrix /app links locally without having
    // to account for it in matrix using keywords
    if (isLocal() && getRoutingMethod() === "hash") {
      appLink = appLink.replace("/app/", "/")
    }

    return (
      <Link to={appLink} className={allClasses.join(" ")} {...otherAttrs}>
        {domToReact(children)}
      </Link>
    )
  }

  if (name === "a" && attribs.href?.startsWith("javascript:")) {
    const { class: className, href: _href, ...otherAttrs } = attribs
    return (
      <button
        className={["anchor-link", className].filter(Boolean).join(" ")}
        type="button"
        {...otherAttrs}
      >
        {domToReact(children, { replace: replaceFunction })}
      </button>
    )
  }

  if (
    name === "a" &&
    attribs.class?.includes("gesb-live-chat-open") &&
    window?.CustomChatEnabled === true
  ) {
    return (
      <button
        className="anchor-link gesb-live-chat-open"
        onClick={() =>
          window._genesys.widgets.bus
            .command("WebChat.open", {
              userData: {},
              form: {
                autoSubmit: false,
                firstname: "",
                lastname: "",
              },
            })
            .done(() => {
              document.getElementById("cx_webchat_form_firstname")?.click()
            })
        }
      >
        {domToReact(children, { replace: replaceFunction })}
      </button>
    )
  }

  return domToReact(node, { replace: replaceFunction })
}

const debugContent = new URLSearchParams(window.location.search).has(
  "contentIds"
)

export function replacePropsReturnXml(
  content: string,
  replaceObj?: Partial<UseContentProps>,
  source?: { id: string | number; prop: string }
) {
  const propObj = replaceObj ?? ({} as UseContentProps)

  const replace =
    debugContent && source
      ? (domNode: any) => {
          if (domNode.type === "tag") {
            domNode.attribs["data-asset-id"] = source.id
            domNode.attribs["data-asset-prop"] = source.prop
          } else if (/^content(Heading|Body|Footer)$/.test(source.prop)) {
            return (
              <span data-asset-id={source.id} data-asset-prop={source.prop}>
                {domNode.data}
              </span>
            )
          }
          return replaceFunction(domNode, { replace })
        }
      : replaceFunction

  const parserOptions = "object" === typeof propObj ? propObj.parserOptions : {}

  return htmlParse(replacePropsInString(content, propObj), {
    trim: true,
    replace,
    ...parserOptions,
  })
}

export function replaceAnchorElement<T extends string>(
  href: T | T[],
  fn: (domNode: Element, href: T) => any
) {
  const search = "string" === typeof href ? [href] : href
  const replace = (domNode: DOMNode) => {
    if (domNode instanceof Element) {
      const match = domNode.attributes.find(
        (item: any) => item.name === "href" && search.includes(item.value)
      )

      if (match) {
        return fn(domNode, match.value as T)
      }
    }

    return replaceFunction(domNode, { replace })
  }

  return replace
}

export function formatUnits(units: any) {
  return Number(units).toFixed(4)
}

export function formatUnitPrice(price: any) {
  return "$" + Number(price).toFixed(4)
}

export function formatAsAtDate(date: string | number | Date, sentence = true) {
  return `${sentence ? "As" : "as"} at ${formatSimpleDate(date)}`
}

// Format dd/mm/yyyy to date
export function formatDMYtoDate(dateStr: string) {
  const [day, month, year] = dateStr.split("/")
  return new Date(+year, +month - 1, +day)
}

export function splitByCamelCase(text: string) {
  return text.replace(/([a-z])([A-Z])/g, "$1 $2")
}

export function formatGrowthStart(date: any) {
  const min = new Date().getFullYear() - 10
  const year = +date.split("/")[2]

  return Math.max(min, year) + ""
}

export function formatMonthlyPremium(data: number) {
  return formatMoney(data / 12)
}

export function toTitleCase(str: string | undefined | null) {
  return "string" === typeof str
    ? str.replace(/\w\S*/g, (txt) => capitalize(txt))
    : ""
}

export function formatAusMobile(number: string | number) {
  const str = ("" + number).replace(/\s/g, "").replace("+61", "0")
  return `${str.substring(0, 4)} ${str.substring(4, 7)} ${str.substring(7)}`
}

// replace object props that are
// null/undefined with empty string
export function replaceNull(obj: any) {
  return Object.keys(obj).reduce(
    (carry: any, key) => ((carry[key] = obj[key] ?? ""), carry),
    {}
  )
}

// takes in a string dollar value and returns float value
export function parseDollarValue(str: string) {
  return parseFloat(str.replace(/[$,]/g, ""))
}

/**
 * Normalise investment option names. Order matters: more
 * specific phrases (e.g. " plan TTR") must run before shorter ones (" plan").
 */
const INVESTMENT_OPTION_NAME_SUFFIXES: readonly string[] = [
  "Mix Your Plan ",
  "Mix Your plan ",
  // " plan TTR",
  " plan TAP",
  " plan RI",
  " plan WSS",
  " plan GSU",
  " plan",
  // " option TTR",
  " option RI",
  " option TAP",
  " option WSS",
  " option GSU",
  " option",
  // " TTR",
  " - RI",
  " - TAP",
  " - WSS",
  " - GSU",
  " RI",
  " TAP",
  " WSS",
  " GSU",
]

/** Masks all but the last 4 digits of an account number, e.g. "340832788" → "XXXXX2788" */
export function maskAccountNumber(accountNumber: string): string {
  const digits = accountNumber.replace(/\D/g, "")
  if (digits.length <= 4) return digits
  return "X".repeat(digits.length - 4) + digits.slice(-4)
}

export function normaliseInvestmentOptionName(
  str: string | null | undefined
): string {
  const acc = str ?? ""
  return INVESTMENT_OPTION_NAME_SUFFIXES.reduce(
    (s, suffix) => s.replace(suffix, ""),
    acc
  )
}

/**
 * Match key for joining holdings to requested allocation rows. Strips product
 * suffixes (including TTR) so TTR holdings align to RI investmentProfile names.
 */
const INVESTMENT_OPTION_MATCH_SUFFIXES: readonly string[] = [
  "Mix Your Plan ",
  "Mix Your plan ",
  " plan TTR",
  " plan TAP",
  " plan RI",
  " plan WSS",
  " plan GSU",
  " plan",
  " option TTR",
  " option RI",
  " option TAP",
  " option WSS",
  " option GSU",
  " option",
  " TTR",
  " - RI",
  " - TAP",
  " - WSS",
  " - GSU",
  " RI",
  " TAP",
  " WSS",
  " GSU",
]

export function investmentOptionMatchKey(
  str: string | null | undefined
): string {
  const acc = (str ?? "").trim()
  return INVESTMENT_OPTION_MATCH_SUFFIXES.reduce(
    (s, suffix) => s.replace(suffix, ""),
    acc
  )
    .replace(/\s+/g, " ")
    .trim()
}
